import React, { FC, useEffect, useState, useRef } from 'react'
import { Divider, message, Select } from 'antd';
import { useTranslation } from "react-i18next";
import { useRemittanceContext } from "@/pages/MyAccounts/QuickPay/QuickPayContext";
import { formatCurrency, deliveryMethodUsableOptions } from "@/utils/utils";
import CurrencyExchange from "../CurrencyExchange/CurrencyExchange";
import { quickPayDeliveryOptions } from '@/utils/defaultData';
import _ from 'lodash';
import styles from "./Amount.module.scss";
import { accountApi } from '@/api'
import moment from 'moment';
import { useParams } from "react-router";
import type { QuickPayPayMent } from "../../QuickPayContext";

interface IAmountProps {
  placement: string;
  detail?: accountApi.IAccountItem;
}

const { Option } = Select;


const Amount: FC<IAmountProps> = ({ placement, detail }) => {
  const { t } = useTranslation();
  const { payment, setPayment } = useRemittanceContext();
  const { prePayData } = payment;
  const params: { id: string } = useParams() || {};
  const accountId = params.id || '';

  // const [transactionType,setTransactionType]=useState<string>("") // 'normal' | 'topup'

  const [dOps, setDOps] = useState(quickPayDeliveryOptions);
  const [validationError, setValidationError] = useState<string | null>(null);

 const getAmountData = async (
  sendingAmount: string,
  sendingCurrency: string,
  instCurrency: string,
  deliveryMethod: number | string,
  transactionType?: string
) => {
  const numericAmount = Number(sendingAmount);
  const numericDeliveryMethod = Number(deliveryMethod);

  const sendMethodType =
    (payment as any)?.sendAccountPaymehodModel?.type ||
    (payment as any)?.sendAccountPaymentMethod?.type ||
    (payment as any)?.sendPaymentMethod?.type ||
    (payment as any)?.paymentMethod?.type ||
    '';

  let transCurrencyForRequest = instCurrency;
  let finalTransactionType = transactionType;

  if (sendMethodType) {
    if (sendMethodType === 'm-pesa') {
      transCurrencyForRequest = 'KES';
    } else if (sendMethodType === 'mtn' || sendMethodType === 'airtel') {
      transCurrencyForRequest = 'UGX';
      finalTransactionType = 'topup';
    }
  }

  if (sendingAmount && sendingCurrency && instCurrency) {
    try {
      const rawData = await accountApi.preQuickPay(accountId, {
        amount: sendingAmount,
        currency: sendingCurrency,
        transCurrency: instCurrency,
        deliveryMethod: numericDeliveryMethod,
        transactionType: finalTransactionType,
      });

      // ---  Add fallback computation for serviceFee + total ---
      const computedServiceFee = rawData?.serviceFee ?? (
        rawData?.commissionPercent
          ? numericAmount * (rawData.commissionPercent / 100)
          : 0
      );

      const finalData = {
        ...rawData,
        serviceFee: computedServiceFee,
        total: rawData?.total ?? (numericAmount + computedServiceFee),
      };
      // ---------------------------------------------------------

      console.log("transaction breakdown (final): ", finalData);

      // WeChat validation
      if (numericDeliveryMethod === 6) {
        if (!finalData || typeof finalData.transAmount === 'undefined') {
          console.error("Missing transAmount in response:", finalData);
          return;
        }

        const amountInCNY = Number(finalData.transAmount);
        if (amountInCNY < 101 || amountInCNY > 50000) {
          const msg = t('WeChat transfer amount must be between 101 CNY and 50,000 CNY');
          message.warning(msg);
          setValidationError(msg);
          setPayment((p: QuickPayPayMent) => ({
            ...p,
            disabled: true,
            prePayData: {},
          }));
          return;
        }
      }

      // Tytrax rule
      if (numericDeliveryMethod === 4 && numericAmount < 151) {
        const msg = t('Tytrax minimum transfer amount is 151 CNY');
        message.warning(msg);
        setValidationError(msg);
        setPayment((p: QuickPayPayMent) => ({
          ...p,
          disabled: true,
          prePayData: {},
        }));
        return;
      }

      setValidationError(null);
      setPayment((p: QuickPayPayMent) => ({
        ...p,
        disabled: false,
        prePayData: finalData, // use computed data
      }));
    } catch (error) {
      setValidationError(t('An error occurred. Please try again.'));
      setPayment((p: QuickPayPayMent) => ({
        ...p,
        disabled: true,
        prePayData: {},
      }));
    }
  }
};


  const fetchDebounceAmountData = useRef(_.debounce(getAmountData, 800)).current;

  useEffect(() => {
    fetchDebounceAmountData(
      payment.sendingAmount,
      payment.sendingCurrency,
      payment.instCurrency,
      payment.deliveryMethod
      
    );
  }, [payment.sendingAmount, payment.sendingCurrency, payment.instCurrency, payment.deliveryMethod, fetchDebounceAmountData]);

  useEffect(() => {
    return () => {
      fetchDebounceAmountData.cancel();
    };
  }, [fetchDebounceAmountData]);

  const paymentLabel = {
    service: t("服务费"),
    pay: t("总付款金额"),
    receive: t("对方可收到"),
    deliveryDate: t("收款日期"),
  };

  const returnAmount = (key: string) => {
    let amount = '';
    switch (key) {
      case 'service':
        amount = formatCurrency(prePayData?.serviceFee ?? 0);
        break;
      case 'pay':
        amount = formatCurrency(prePayData?.total ?? 0);
        break;
      case 'receive':
        amount = formatCurrency(prePayData?.transAmount ?? 0);
        break;
      case 'deliveryDate':
        amount = moment().format('DD-MM-YYYY');
        break;
      default:
    }
    return amount;
  };

  const optionItem = (v: any, sts: React.CSSProperties) =>
    <div className={`flex-center`} style={sts}>
      <span>{v.label}</span>
    </div>

  const returnPaymentEl = () => Object.entries(paymentLabel).map(([key, value], index) =>
    <div key={key} className={styles.payment}>
      <span className={styles.paymentKey} style={{ fontSize: index === 1 ? 16 : 14 }}>{value}</span>
      <span className={styles.paymentValue}>
        <span style={index === 1 ? { fontSize: 22, fontWeight: 600 } : {}}>{returnAmount(key)} </span>
        {key !== 'deliveryDate' && <span>{key === 'receive' ? payment.instCurrency : payment.sendingCurrency}</span>}
      </span>
    </div>
  );

  useEffect(() => {
    setDOps(_.cloneDeep(quickPayDeliveryOptions));
  }, [payment.instCurrency]);

  return (
    <div className={`${styles.amountRoot} flex-column-center`}>
      <div className={styles.content}>
        <CurrencyExchange onChange={(t) => { }} />
        <div className={styles.paymentMethod}>
          <span>{t("汇款方法")}</span>
          <div>
            <div>{detail?.name}</div>
            <div>{t("可用余额")}: {formatCurrency(detail?.balance || 0)} {detail?.currency}</div>
          </div>
        </div>
        <div className={styles.method}>
          <span className={styles.label}>{t("收款方式")}</span>
          <Select
            optionLabelProp="label"
            dropdownMatchSelectWidth={false}
            getPopupContainer={(triggerNode: { parentNode: any; }) => triggerNode.parentNode || document.body}
            value={Number(payment.deliveryMethod)}
            onChange={(e: number | string) => {
              const value = Number(e);
              const current = dOps.find(v => value === v.value);
              setPayment({
                ...payment,
                deliveryMethod: value,
                deliveryMethodName: current?.label || '',
                beneficiaryNo: '',
                recipientName: ''
              });
            }}
          >
            {
              dOps.map(v => {
                const usableOptions = deliveryMethodUsableOptions(payment.instCurrency);
                return (
                  <Option
                    value={v.value}
                    key={v.value}
                    disabled={!usableOptions.includes(v.value)}
                    label={optionItem(v, { height: '100%' })}
                  >
                    {optionItem(v, { color: 'inherit' })}
                  </Option>
                )
              })
            }
          </Select>
        </div>
        <Divider />
        <Divider />
        {returnPaymentEl()}
       
      </div>
    </div>
  )
}

export default Amount;
